import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { FunnelIcon, PushPinIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { GetDemandFilterOptionsResponse } from 'src/api/rpc/hooks/procurement/useDemandForecast';
import { DemandFilterControl } from './DemandFilterControl';
import { useDemandFilterLabels } from './demandFilterLabels';
import { cn } from '@/lib/utils';
import { useTableFilterPreferences } from 'src/hooks/useTableFilterPreferences';
import { AddButton, CHIP_CONTROL, ControlRow } from './ControlRow';
import {
  activeFilterKeys,
  clearAllFilters,
  clearFilter,
  DEFAULT_FILTER_KEYS,
  FILTER_KEYS,
  PINNED_FILTERS_TABLE_ID,
  PinnedFiltersSchema,
  TEXT_FILTER_KEYS,
  type DemandSearch,
  type FilterKey,
  type TextFilterKey,
} from './demandFilters';

/**
 * Addable filter bar: nothing is shown until it is added, so the default page is
 * one line rather than nine empty controls. Adding a filter reveals its control
 * and nothing else; every value change writes straight to the URL.
 */
interface Props {
  search: DemandSearch;
  options: GetDemandFilterOptionsResponse | undefined;
  onChange: (next: DemandSearch) => void;
  /**
   * The filters this screen offers, for a screen that applies only some of
   * the shared set (the buyer board). The pin set stays the user's one set:
   * pins outside `keys` are kept for the screens that offer them.
   */
  keys?: readonly FilterKey[];
}

function isTextFilter(key: FilterKey): key is TextFilterKey {
  return (TEXT_FILTER_KEYS as readonly FilterKey[]).includes(key);
}

/** Pinned filters expose unpin; unpinned filters expose pin then remove. */
function FilterSlot({
  label,
  pinLabel,
  removeLabel,
  pinned,
  onTogglePin,
  onRemove,
  children,
}: React.PropsWithChildren<{
  label: string;
  /** Announced to screen readers, so it is user-visible copy and translated. */
  pinLabel: string;
  removeLabel: string;
  pinned: boolean;
  onTogglePin: () => void;
  onRemove: () => void;
}>) {
  return (
    <div
      className={cn(
        'flex h-7 max-w-full items-center gap-1 rounded-md border bg-background pl-2 pr-0.5',
        CHIP_CONTROL,
      )}
    >
      <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
        {label}
      </span>
      <div className="min-w-0">{children}</div>
      <Button
        type="button"
        onClick={onTogglePin}
        aria-pressed={pinned}
        aria-label={pinLabel}
        title={pinLabel}
        variant="ghost"
        size="icon"
        className={cn(
          'size-6 rounded-sm [&_svg]:size-3!',
          pinned ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <PushPinIcon weight={pinned ? 'fill' : 'regular'} />
      </Button>
      {!pinned ? (
        <Button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel}
          title={removeLabel}
          variant="ghost"
          size="icon"
          className="size-6 rounded-sm text-muted-foreground [&_svg]:size-3"
        >
          <XIcon />
        </Button>
      ) : null}
    </div>
  );
}

function useTextFilterDrafts(search: DemandSearch) {
  // One uncommitted string per free-text filter. They are the only filter
  // state outside the URL: a half-typed term is not a query yet.
  const [drafts, setDrafts] = useState<Record<TextFilterKey, string>>({
    skuSearch: search.skuSearch ?? '',
    textSearch: search.textSearch ?? '',
  });
  const [previousText, setPreviousText] = useState({
    skuSearch: search.skuSearch,
    textSearch: search.textSearch,
  });
  if (
    previousText.skuSearch !== search.skuSearch ||
    previousText.textSearch !== search.textSearch
  ) {
    setPreviousText({
      skuSearch: search.skuSearch,
      textSearch: search.textSearch,
    });
    setDrafts({
      skuSearch:
        previousText.skuSearch !== search.skuSearch
          ? (search.skuSearch ?? '')
          : drafts.skuSearch,
      textSearch:
        previousText.textSearch !== search.textSearch
          ? (search.textSearch ?? '')
          : drafts.textSearch,
    });
  }

  return { drafts, setDrafts };
}

export function DemandFilterBar({
  search,
  options,
  onChange,
  keys = FILTER_KEYS,
}: Props) {
  const gt = useGT();
  // Pinned filters are a per-user preference, not URL state: which boxes you
  // keep on the bar is about you, not about the view you are sharing. Until the
  // stored set loads, fall back to the defaults rather than an empty bar.
  const { savedFilters, saveFilters, isPending } = useTableFilterPreferences(
    PINNED_FILTERS_TABLE_ID,
    PinnedFiltersSchema,
  );
  const offered = new Set<FilterKey>(keys);
  const allPinned = savedFilters?.pinned ?? DEFAULT_FILTER_KEYS;
  const pinned = allPinned.filter((key) => offered.has(key));
  // Membership is tested once per filter while rendering the bar, so a Set
  // rather than repeated linear scans of the pin list.
  const isPinned = new Set<FilterKey>(pinned);
  /**
   * Filters added from `+` for this visit only.
   *
   * Adding and pinning are deliberately different acts: `+` puts a filter on
   * the bar now, the pin says "keep it there next time". Making `+` pin as well
   * collapsed the two into one, and then the pin toggle and the `×` did the
   * same thing as each other.
   */
  const [temporary, setTemporary] = useState<FilterKey[]>([]);
  const [displayOrder, setDisplayOrder] = useState<FilterKey[]>([]);
  const { drafts, setDrafts } = useTextFilterDrafts(search);

  const labels = useDemandFilterLabels();

  const active = new Set(
    activeFilterKeys(search).filter((key) => offered.has(key)),
  );
  // Keep the visible order through edits and pin changes. New filters append;
  // saved pins seed the order when opening the page.
  const onScreen = new Set<FilterKey>([...pinned, ...temporary, ...active]);
  const shown = [...new Set([...displayOrder, ...onScreen])].filter((key) =>
    onScreen.has(key),
  );
  const addable = keys
    .filter((key) => !onScreen.has(key))
    .sort((a, b) => labels[a].localeCompare(labels[b]));

  function rememberDisplayOrder(next: FilterKey[]) {
    // The fallback pins are provisional; loaded preferences own their order.
    if (!isPending) setDisplayOrder(next);
  }

  function setPinned(next: FilterKey[]) {
    // Writing before the stored value has loaded would clobber it.
    if (isPending) return;
    const nextPins = new Set(next);
    saveFilters({
      pinned: [
        ...allPinned.filter((key) => !offered.has(key)),
        ...shown.filter((key) => nextPins.has(key)),
      ],
    });
  }

  // Any filter change resets paging: page 4 of the old result set is not page 4
  // of the new one, and an out-of-range page renders an empty table.
  function apply(next: DemandSearch) {
    onChange({ ...next, page: undefined });
  }

  /** `×` takes the filter off the bar entirely — value, pin and all. */
  function removeFilter(key: FilterKey) {
    rememberDisplayOrder(shown.filter((other) => other !== key));
    setTemporary((keys) => keys.filter((other) => other !== key));
    if (isPinned.has(key)) setPinned(pinned.filter((other) => other !== key));
    if (isTextFilter(key)) {
      setDrafts((current) => ({ ...current, [key]: '' }));
    }
    apply(clearFilter(search, key));
  }

  /**
   * The pin only changes whether the filter comes back next time. Pinning
   * something added this visit promotes it out of `temporary`; unpinning leaves
   * it on the bar for the rest of the visit, because taking it off the screen
   * is what `×` is for.
   */
  function togglePin(key: FilterKey) {
    if (isPending) return;
    rememberDisplayOrder(shown);
    if (isPinned.has(key)) {
      setPinned(pinned.filter((other) => other !== key));
      setTemporary((keys) => (keys.includes(key) ? keys : [...keys, key]));
      return;
    }
    setPinned([...pinned, key]);
    setTemporary((keys) => keys.filter((other) => other !== key));
  }

  const addMenu = (
    <Popover>
      <PopoverTrigger asChild>
        <AddButton
          label={gt('Add a filter', { context: 'demand filter bar add' })}
          disabled={addable.length === 0}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-0">
        <Command>
          <CommandInput
            placeholder={gt('Filter by…', {
              context: 'demand filter search placeholder',
            })}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty>
              <T context="demand filter none left">No filters left.</T>
            </CommandEmpty>
            <CommandGroup>
              {addable.map((key) => (
                <CommandItem
                  key={key}
                  value={labels[key]}
                  onSelect={() => {
                    rememberDisplayOrder([...shown, key]);
                    setTemporary((keys) => [...new Set([...keys, key])]);
                  }}
                  className="text-xs"
                >
                  <FunnelIcon className="mr-2 h-3 w-3" />
                  {labels[key]}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  return (
    <ControlRow
      label={gt('Filter', { context: 'demand control row label' })}
      add={addMenu}
    >
      {shown.map((key) => (
        <FilterSlot
          key={key}
          label={labels[key]}
          pinLabel={
            isPinned.has(key)
              ? gt('Unpin the {filter} filter', {
                  context: 'demand filter pin toggle',
                  filter: labels[key],
                })
              : gt('Pin the {filter} filter to the bar', {
                  context: 'demand filter pin toggle',
                  filter: labels[key],
                })
          }
          removeLabel={gt('Remove the {filter} filter', {
            context: 'demand filter remove',
            filter: labels[key],
          })}
          pinned={isPinned.has(key)}
          onTogglePin={() => togglePin(key)}
          onRemove={() => removeFilter(key)}
        >
          <DemandFilterControl
            filterKey={key}
            search={search}
            options={options}
            apply={apply}
            draft={isTextFilter(key) ? drafts[key] : ''}
            onDraftChange={(value) => {
              if (isTextFilter(key)) {
                setDrafts((current) => ({ ...current, [key]: value }));
              }
            }}
          />
        </FilterSlot>
      ))}

      {shown.length > 0 ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-muted-foreground"
          onClick={() => {
            // Values only. The pin set is the user's layout and survives a
            // clear — that is the difference between the two controls. Filters
            // added just for this visit go with the values.
            rememberDisplayOrder(shown.filter((key) => isPinned.has(key)));
            setTemporary([]);
            setDrafts({ skuSearch: '', textSearch: '' });
            apply(clearAllFilters(search, keys));
          }}
        >
          <T context="demand filter bar clear">Clear all</T>
        </Button>
      ) : null}
    </ControlRow>
  );
}
